import { useAuth } from "@stardeck-customer-apps/project-auth";
import { Lock, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  const navigation = [
    { name: "HOME", path: "/" },
    { name: "ROOMS", path: "/rooms" },
    { name: "DINING", path: "/dining" },
    { name: "SPA", path: "/spa" },
    { name: "GALLERY", path: "/gallery" },
    { name: "ABOUT", path: "/about" },
    { name: "CONTACT", path: "/contact" },
  ];

  // Check if user has admin access by testing admin endpoint
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user || isLoading) {
        setIsAdmin(false);
        return;
      }

      try {
        const response = await fetch("/api/admin-only");
        setIsAdmin(response.ok);
      } catch {
        setIsAdmin(false);
      }
    };

    checkAdminAccess();
  }, [user, isLoading]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200">
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="font-heading text-2xl font-bold text-stone-800 tracking-tight">
            THE AMOR
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm tracking-widest transition-colors ${
                  isActive(item.path)
                    ? "text-stone-900 font-semibold"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                {item.name}
              </Link>
            ))}
            {/* Admin Link - Only show for authenticated admin users */}
            {isAdmin && (
              <Link
                to="/admin/reservations"
                className={`text-sm tracking-widest transition-colors flex items-center gap-1.5 ${
                  location.pathname.startsWith("/admin")
                    ? "text-stone-900 font-semibold"
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                <Lock className="size-3" />
                ADMIN
              </Link>
            )}
          </div>

          {/* Book Now Button */}
          <Link
            to="/book"
            className="hidden lg:block bg-stone-800 text-white px-5 py-2.5 text-xs tracking-widest uppercase font-semibold hover:bg-stone-700 transition-colors"
          >
            Book Now
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-stone-800"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-stone-200 pt-4">
            <div className="flex flex-col gap-4">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-sm tracking-widest transition-colors ${
                    isActive(item.path) ? "text-stone-900 font-semibold" : "text-stone-600"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              {/* Admin Link - Only show for authenticated admin users */}
              {isAdmin && (
                <Link
                  to="/admin/reservations"
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-sm tracking-widest transition-colors flex items-center gap-1.5 ${
                    location.pathname.startsWith("/admin")
                      ? "text-stone-900 font-semibold"
                      : "text-stone-600"
                  }`}
                >
                  <Lock className="size-3" />
                  ADMIN
                </Link>
              )}
              <Link
                to="/book"
                onClick={() => setIsMenuOpen(false)}
                className="bg-stone-800 text-white px-5 py-2.5 text-xs tracking-widest uppercase font-semibold text-center hover:bg-stone-700 transition-colors"
              >
                Book Now
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
